import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  useEffect,
  useRef,
  type CSSProperties,
  type RefObject,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { useOrg } from '../app/OrgProvider';
import { useAuth } from '../auth/AuthProvider';
import { ClapperRule, Wordmark } from '../components/Clapper';
import { AppLink, useGo } from '../components/Nav';
import { DownloadIcon, LinkIcon, LockIcon, RestartIcon, WaveformIcon } from '../components/icons';
import {
  Badge,
  Banner,
  Button,
  Loading,
  Spinner,
  cx,
  Input,
  Textarea,
  ErrorText,
} from '../components/ui';
import { track } from '../lib/amplitude';
import { ApiError, errorMessage } from '../lib/api';
import { canonicalUrl, onCustomDomain, shareUrl } from '../lib/env';
import { dateTime, timeAgo, timecode } from '../lib/format';
import type { Playback, ShownLink } from '../lib/types';
import { AudioPlayer } from '../player/AudioPlayer';
import { Comments } from '../player/Comments';
import { Player } from '../player/Player';
import { ReactionPill } from '../player/ReactionPill';
import { SignInPrompt } from '../player/SignInPrompt';
import type { ReactionsState } from '../player/useReactions';
import { ShareSettings } from '../player/ShareSettings';
import { SlateClip } from '../player/SlateClip';
import { Transcript } from '../player/Transcript';
import { useReactions } from '../player/useReactions';
import { useViewBeacon } from '../player/useViewBeacon';
import { fetchMe } from '../queries/account';
import { qk } from '../queries/keys';
import { fetchShare, giveViewerEmail, postComment } from '../queries/share';
import { canManageVideo, deleteVideo, fetchVideo, updateVideo } from '../queries/videos';

/**
 * /v/:shareId. The page everybody who is not a Slate user sees.
 *
 * It has to work with no account, on a phone, on the first tap. It also has to
 * work *before the recording is ready*: the share link is handed out the
 * instant recording starts, so arriving early is normal, not an error.
 *
 * `kind` decides which player mounts. An audio note has no HLS ladder and no
 * poster, so it never touches hls.js and never renders a video element with
 * nothing in it. Everything around the player (comments, the view beacon, the
 * copy button) is the same either way.
 */

/**
 * The owner asked who is watching. Required: this stands where the video
 * would be. Asked: a bar under it, with a way to say no.
 */
function EmailGate({
  shareId,
  title,
  posterUrl,
  required = false,
  onDone,
}: {
  shareId: string;
  title: string;
  posterUrl: string | null;
  required?: boolean;
  onDone: () => void;
}) {
  const [email, setEmail] = useState('');
  const [skipped, setSkipped] = useState(false);
  const give = useMutation({
    mutationFn: () => giveViewerEmail(shareId, email),
    onSuccess: () => {
      // That an email was given, never the address itself.
      track('Viewer Email Given', { video_id: shareId });
      onDone();
    },
  });
  if (skipped) return null;

  const form = (
    <form
      className="flex flex-col gap-2 sm:flex-row sm:items-end"
      onSubmit={(event) => {
        event.preventDefault();
        give.mutate();
      }}
    >
      <div className="flex-1">
        <Input
          label="Your email"
          type="email"
          value={email}
          required
          autoComplete="email"
          onChange={(event) => setEmail(event.target.value)}
        />
      </div>
      <Button type="submit" variant="primary" busy={give.isPending}>
        {required ? 'Watch' : 'Send'}
      </Button>
      {!required ? (
        <Button type="button" onClick={() => setSkipped(true)}>
          Not now
        </Button>
      ) : null}
    </form>
  );

  if (!required) {
    return (
      <div className="rounded-md border border-hairline bg-slate-900/60 p-4">
        <p className="text-body text-chalk">
          The person who recorded this would like to know who watched.
        </p>
        <div className="mt-3">{form}</div>
        {give.isError ? (
          <div className="mt-2">
            <ErrorText>{errorMessage(give.error)}</ErrorText>
          </div>
        ) : null}
      </div>
    );
  }

  return (
    <div className="relative overflow-hidden rounded-md border border-hairline bg-slate-900">
      {posterUrl ? (
        <img
          src={posterUrl}
          alt=""
          className="absolute inset-0 h-full w-full object-cover opacity-40"
        />
      ) : null}
      <div className="relative flex aspect-video w-full flex-col items-center justify-center gap-4 px-6 text-center">
        <div>
          <p className="text-h4 text-chalk">{title}</p>
          <p className="mt-1 max-w-sm text-body text-chalk-dim">
            Enter your email to watch this recording.
          </p>
        </div>
        <div className="w-full max-w-sm text-left">{form}</div>
        {give.isError ? <ErrorText>{errorMessage(give.error)}</ErrorText> : null}
      </div>
    </div>
  );
}

/**
 * Past the free plan's rolling window. The frame is there and dimmed so it
 * is clearly a real recording; the words say whose plan is in the way and
 * what would fix it. The owner gets the fix; a visitor gets told who to ask.
 */
function PlanWall({
  title,
  posterUrl,
  limit,
  owner,
  authorName,
}: {
  title: string;
  posterUrl: string | null;
  limit: number;
  owner: boolean;
  authorName: string | null;
}) {
  return (
    <div className="relative overflow-hidden rounded-md border border-hairline bg-slate-900">
      {posterUrl ? (
        <img src={posterUrl} alt="" className="absolute inset-0 h-full w-full object-cover opacity-25 blur-[2px]" />
      ) : null}
      <div className="absolute inset-0 bg-slate-950/55" />
      <div className="relative flex aspect-video w-full flex-col items-center justify-center gap-4 px-6 text-center">
        <span className="flex h-12 w-12 items-center justify-center rounded-full bg-slate-950/80 text-chalk">
          <LockIcon size={20} />
        </span>
        <div>
          <p className="text-h4 text-chalk">{title}</p>
          <p className="mt-1.5 max-w-md text-body text-chalk-dim">
            {owner
              ? `The free plan keeps your ${limit} most recent recordings playable, and this one is older than that. Make room by deleting newer ones, or start a team and everything unlocks.`
              : `${authorName?.trim() || 'The owner'} is on the free plan, which keeps only their ${limit} most recent recordings playable. Ask them to unlock it.`}
          </p>
        </div>
        {owner ? (
          <div className="flex flex-wrap items-center justify-center gap-2">
            <AppLink to="/settings/subscription">
              <Button variant="primary">Start a team</Button>
            </AppLink>
            <AppLink to="/">
              <Button>Manage recordings</Button>
            </AppLink>
          </div>
        ) : null}
      </div>
    </div>
  );
}

/** A small round action with an icon and a tooltip. */
function IconAction({ label, onClick, children }: { label: string; onClick: () => void; children: ReactNode }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={label}
      title={label}
      className="flex h-8 w-8 items-center justify-center rounded text-chalk-dim hover:bg-slate-800 hover:text-chalk"
    >
      {children}
    </button>
  );
}

/**
 * When the video ends: a reply box, the reactions, and Watch again, laid
 * over the last frame. The moment right after watching is when a person
 * has something to say.
 */
function EndCard({
  shareId,
  authorName,
  reactions,
  reactionsEnabled,
  mediaRef,
  onWatchAgain,
  onCommented,
  guard,
}: {
  shareId: string;
  authorName: string | null;
  reactions: ReactionsState;
  reactionsEnabled: boolean;
  mediaRef: RefObject<HTMLMediaElement | null>;
  onWatchAgain: () => void;
  onCommented: () => void;
  guard: (purpose: 'react' | 'comment', then: () => void) => void;
}) {
  const { user } = useAuth();
  const [body, setBody] = useState('');
  const [sent, setSent] = useState(false);
  const who = authorName?.trim() ? authorName.trim().split(' ')[0] : null;
  const post = useMutation({
    mutationFn: () => postComment(shareId, { bodyText: body.trim() }),
    onSuccess: () => {
      track('Comment Posted', { video_id: shareId, signed_in: Boolean(user) });
      setSent(true);
      setBody('');
      onCommented();
    },
  });
  const canSend = body.trim().length > 0;

  return (
    <div className="absolute inset-0 z-20 flex flex-col items-center justify-center gap-4 bg-slate-950/85 px-6 text-center">
      <div className="w-full max-w-md text-left">
        <p className="text-body font-medium text-chalk">{who ? `Reply to ${who}` : 'Leave a reply'}</p>
        {sent ? (
          <p className="mt-2 rounded-md border border-hairline bg-slate-900 p-4 text-body text-chalk-dim">
            Sent. {who ? `${who} will see it on this moment.` : 'It is on the recording now.'}
          </p>
        ) : (
          <form
            className="mt-2 rounded-md border border-hairline bg-slate-900 p-3"
            onSubmit={(event) => {
              event.preventDefault();
              if (canSend) guard('comment', () => post.mutate());
            }}
          >
            <textarea
              rows={3}
              value={body}
              maxLength={4000}
              placeholder={who ? `Thank you, ${who}!` : 'Thanks for this.'}
              onFocus={() => {
                if (!user) guard('comment', () => {});
              }}
              onChange={(event) => setBody(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === ' ') event.stopPropagation();
              }}
              className="w-full resize-none bg-transparent text-body text-chalk placeholder:text-chalk-faint focus:outline-none"
            />
            <div className="mt-2 flex items-center gap-2">
              <span className="flex-1" />
              <Button type="submit" variant="primary" size="sm" busy={post.isPending} disabled={!canSend}>
                Comment
              </Button>
            </div>
            {post.isError ? <ErrorText>{errorMessage(post.error)}</ErrorText> : null}
          </form>
        )}
      </div>
      {reactionsEnabled ? <ReactionPill state={reactions} mediaRef={mediaRef} ready compact guard={guard} /> : null}
      <button type="button" onClick={onWatchAgain} className="flex items-center gap-2 text-body text-chalk hover:underline">
        <RestartIcon size={16} />
        Watch again
      </button>
    </div>
  );
}

/** Tabs across the top of the side panel; one child at a time. */
function SidePanel({
  tabs,
  initial,
  forced,
  onChange,
  children,
}: {
  tabs: Array<{ key: string; label: string }>;
  initial: string;
  /** Switch to this tab when `at` changes (a button elsewhere asked). */
  forced?: { key: string; at: number } | null;
  /** A tab was pressed: the one chosen, and the one left behind. */
  onChange?: (next: string, previous: string) => void;
  children: (tab: string) => ReactNode;
}) {
  const [tab, setTab] = useState(initial);
  useEffect(() => {
    if (forced) setTab(forced.key);
  }, [forced]);
  const active = tabs.some((t) => t.key === tab) ? tab : (tabs[0]?.key ?? initial);
  return (
    <div>
      <div className="flex gap-1 border-b border-hairline" role="tablist">
        {tabs.map((t) => (
          <button
            key={t.key}
            type="button"
            role="tab"
            aria-selected={active === t.key}
            onClick={() => {
              onChange?.(t.key, active);
              setTab(t.key);
            }}
            className={cx(
              '-mb-px border-b-2 px-3 py-2 text-body transition-colors',
              active === t.key
                ? 'border-chalk text-chalk'
                : 'border-transparent text-chalk-faint hover:text-chalk-dim',
            )}
          >
            {t.label}
          </button>
        ))}
      </div>
      <div className="pt-5">{children(active)}</div>
    </div>
  );
}

/** The free tier has no transcript. Said once, to the owner, with the way to one. */
function TranscriptUpsell() {
  return (
    <div>
      <h2 className="text-h4 text-chalk">Transcript</h2>
      <p className="mt-2 text-body text-chalk-dim">
        Captions and a searchable transcript come with a team plan.{' '}
        <AppLink to="/settings/subscription" className="text-signal hover:underline">
          Start a team
        </AppLink>
        .
      </p>
    </div>
  );
}

/**
 * The pages that were on screen, each once, in the order they came up.
 * From the Mac app, which notes which page the browser in front is showing
 * as it records. A link opens the page; its time jumps the player to where
 * it came up. The owner can take a page off the list, and can keep the
 * whole list back until the recording has played, for a walkthrough that
 * should be watched before it is skimmed.
 */
function LinksBlock({
  shareId,
  videoId,
  links,
  atEnd,
  mediaRef,
}: {
  shareId: string;
  videoId: string | null;
  links: ShownLink[];
  /** Hold the list until the recording has played through. */
  atEnd: boolean;
  mediaRef: RefObject<HTMLMediaElement | null>;
}) {
  const client = useQueryClient();
  const [played, setPlayed] = useState(false);
  useEffect(() => {
    if (!atEnd) return;
    const media = mediaRef.current;
    if (!media) return;
    const done = () => setPlayed(true);
    media.addEventListener('ended', done);
    return () => media.removeEventListener('ended', done);
  }, [atEnd, mediaRef, links.length]);
  const remove = useMutation({
    mutationFn: (url: string) =>
      updateVideo(videoId as string, { hiddenLinks: [...hiddenSoFar.current, url] }).then(() => {
        hiddenSoFar.current.push(url);
      }),
    onSuccess: () => {
      track('Shown Link Hidden', { video_id: shareId });
      void client.invalidateQueries({ queryKey: qk.share(shareId) });
    },
  });
  // The API takes the whole hidden list each time; what this page has
  // removed so far is what it knows of that list.
  const hiddenSoFar = useRef<string[]>([]);

  if (links.length === 0) return null;
  const editable = videoId !== null;
  const held = atEnd && !played;

  return (
    <section aria-labelledby="links-heading" className="rounded-md border border-hairline bg-slate-900/40 px-4 py-3">
      <h2 id="links-heading" className="text-caption font-medium uppercase tracking-wide text-chalk-faint">
        Links
      </h2>
      {held ? (
        <p className="mt-1.5 text-caption text-chalk-faint">
          {links.length === 1 ? 'One page' : `${links.length} pages`} shown in this recording. They appear once it has played.
        </p>
      ) : (
        <ul className="mt-2 space-y-1.5">
          {links.map((link) => (
            <li key={link.url} className="group flex items-center gap-2.5">
              <img src={link.favicon} alt="" width={16} height={16} className="h-4 w-4 shrink-0 rounded-sm" loading="lazy" />
              <a
                href={link.url}
                target="_blank"
                rel="noopener noreferrer"
                className="min-w-0 flex-1 truncate text-body text-chalk hover:underline"
                title={link.url}
              >
                {link.title ?? link.url.replace(/^https?:\/\//, '')}
              </a>
              <button
                type="button"
                data-timecode
                className="shrink-0 font-mono text-caption text-chalk-faint hover:text-chalk"
                title="Jump to where this page came up"
                onClick={() => {
                  const media = mediaRef.current;
                  if (!media) return;
                  media.currentTime = link.t;
                  void media.play();
                }}
              >
                {timecode(link.t)}
              </button>
              {editable ? (
                <button
                  type="button"
                  aria-label={`Remove ${link.host} from the links`}
                  title="Remove"
                  className="shrink-0 rounded px-1 text-chalk-faint opacity-0 hover:text-chalk group-hover:opacity-100 focus:opacity-100"
                  disabled={remove.isPending}
                  onClick={() => remove.mutate(link.url)}
                >
                  ×
                </button>
              ) : null}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

/**
 * What the recording covers, in a few sentences, under the player. Written
 * from the transcript the moment there is one, so it usually exists before
 * the first viewer arrives; the owner can rewrite it, or clear it, and what
 * they wrote stays. Nothing is shown to a viewer until there is something
 * to show.
 */
function SummaryBlock({
  shareId,
  videoId,
  summary,
  pending,
}: {
  shareId: string;
  /** The library id, when the person looking may edit. */
  videoId: string | null;
  summary: string | null;
  /** The transcript is on its way, so a summary is too. */
  pending: boolean;
}) {
  const client = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(summary ?? '');
  const save = useMutation({
    mutationFn: (text: string) => updateVideo(videoId as string, { summary: text }),
    onSuccess: () => {
      // That it was rewritten, never what it says.
      track('Summary Edited', { video_id: shareId });
      setEditing(false);
      void client.invalidateQueries({ queryKey: qk.share(shareId) });
    },
  });
  const editable = videoId !== null;
  if (!summary && !editable && !pending) return null;

  return (
    <section aria-labelledby="summary-heading" className="rounded-md border border-hairline bg-slate-900/40 px-4 py-3">
      <div className="flex items-center justify-between gap-3">
        <h2 id="summary-heading" className="text-caption font-medium uppercase tracking-wide text-chalk-faint">
          Summary
        </h2>
        {editable && !editing ? (
          <button
            type="button"
            className="text-caption text-chalk-dim hover:text-chalk"
            onClick={() => {
              setDraft(summary ?? '');
              setEditing(true);
            }}
          >
            {summary ? 'Edit' : 'Add a summary'}
          </button>
        ) : null}
      </div>
      {editing ? (
        <form
          className="mt-2 space-y-2"
          onSubmit={(event) => {
            event.preventDefault();
            save.mutate(draft);
          }}
        >
          <Textarea
            rows={4}
            value={draft}
            onChange={(event) => setDraft(event.target.value)}
            placeholder="What this recording covers, for someone deciding whether to watch."
            maxLength={4000}
            autoFocus
          />
          {save.isError ? <ErrorText>{errorMessage(save.error)}</ErrorText> : null}
          <div className="flex items-center gap-2">
            <Button type="submit" size="sm" disabled={save.isPending}>
              {save.isPending ? 'Saving' : 'Save'}
            </Button>
            <button
              type="button"
              className="text-caption text-chalk-dim hover:text-chalk"
              onClick={() => setEditing(false)}
              disabled={save.isPending}
            >
              Cancel
            </button>
          </div>
        </form>
      ) : summary ? (
        <p className="mt-1.5 whitespace-pre-line text-body text-chalk-dim">{summary}</p>
      ) : pending ? (
        <div role="status" aria-label="Being written from the transcript">
          <div className="mt-2.5 space-y-2" aria-hidden>
            <div className="shimmer h-3 w-11/12 rounded" />
            <div className="shimmer h-3 w-4/5 rounded" />
            <div className="shimmer h-3 w-3/5 rounded" />
          </div>
          <p className="mt-2.5 text-caption text-chalk-faint">Being written from the transcript.</p>
        </div>
      ) : (
        <p className="mt-1.5 text-caption text-chalk-faint">
          Nothing yet. One is written from the transcript when there is one.
        </p>
      )}
    </section>
  );
}

/**
 * While there is nothing to play yet. With a first frame from the recorder
 * it looks like a video about to start, which is what it is; without one it
 * says so in words.
 */
function ProcessingPanel({
  title,
  audio,
  posterUrl,
}: {
  title: string;
  audio: boolean;
  posterUrl: string | null;
}) {
  return (
    <div className="relative overflow-hidden rounded-md border border-hairline bg-slate-900">
      {posterUrl ? (
        <img src={posterUrl} alt="" className="absolute inset-0 h-full w-full object-cover" />
      ) : null}
      <div
        className="clapper-stripes absolute inset-x-0 top-0 h-2 opacity-[0.12]"
        aria-hidden="true"
      />
      <div
        className={cx(
          'relative flex w-full flex-col items-center justify-center gap-3 px-6 text-center',
          audio ? 'py-14' : 'aspect-video',
          posterUrl ? 'bg-slate-950/55' : '',
        )}
      >
        <Spinner size={22} className={posterUrl ? 'text-chalk' : 'text-chalk-faint'} />
        <p className="text-h4 text-chalk">{title}</p>
        <p className="max-w-sm text-body text-chalk-dim">
          {posterUrl
            ? 'Almost there. It will start on its own in a moment.'
            : 'This one is still being processed. It usually takes a minute or two. Leave this page open and it will start on its own.'}
        </p>
      </div>
    </div>
  );
}

/**
 * Delete, at the bottom of the owner's settings panel. Two presses, and the
 * copy says where it actually goes: the archive, not the void. Afterwards
 * there is no page left to stand on, so it hands the person their library.
 */
function DeleteRecording({ videoId, shareId }: { videoId: string; shareId: string }) {
  const go = useGo();
  const queryClient = useQueryClient();
  const [confirming, setConfirming] = useState(false);

  const remove = useMutation({
    mutationFn: () => deleteVideo(videoId),
    onSuccess: () => {
      track('Video Deleted', { video_id: shareId });
      void queryClient.invalidateQueries({ queryKey: ['videos'] });
      void queryClient.invalidateQueries({ queryKey: qk.video(videoId) });
      go('/', { replace: true });
    },
  });

  return (
    <div className="mt-2 border-t border-hairline pt-4">
      <p className="text-body text-chalk">Delete this recording</p>
      <p className="mt-0.5 text-caption text-chalk-faint">
        It goes to your archive and this link stops working. You have thirty days to bring it back.
      </p>
      {confirming ? (
        <div className="mt-3 flex items-center gap-2">
          <Button variant="danger" size="sm" busy={remove.isPending} onClick={() => remove.mutate()}>
            Delete
          </Button>
          <Button variant="ghost" size="sm" onClick={() => setConfirming(false)}>
            Keep
          </Button>
        </div>
      ) : (
        <Button variant="danger" size="sm" className="mt-3" onClick={() => setConfirming(true)}>
          Delete
        </Button>
      )}
      {remove.isError ? (
        <div className="mt-2">
          <ErrorText>{errorMessage(remove.error)}</ErrorText>
        </div>
      ) : null}
    </div>
  );
}

export function SharePage({ shareId }: { shareId: string }) {
  const { user, loading: authLoading } = useAuth();
  const videoRef = useRef<HTMLVideoElement>(null);
  const audioRef = useRef<HTMLAudioElement>(null);

  const query = useQuery({
    // Keyed on who is asking: the owner gets their original upload while
    // the ladder is cut, a visitor does not. And not before Firebase has
    // said who that is, or the first answer is the visitor's.
    queryKey: [...qk.share(shareId), user?.uid ?? 'anon'],
    queryFn: () => fetchShare(shareId),
    enabled: !authLoading,
    // Poll while the pipeline works, then stop. `false` turns polling off.
    refetchInterval: (result) => {
      const data = result.state.data;
      const busy = data?.status === 'processing' || data?.status === 'uploading';
      // Captions land a little after the video; keep asking until they do.
      const listening = data?.status === 'ready' && data.transcript?.status === 'pending';
      const rendering = data?.rendering === true;
      return busy || rendering ? 5000 : listening ? 10000 : false;
    },
    retry: (failureCount, error) =>
      !(error instanceof ApiError && error.isNotFound) && failureCount < 2,
  });

  const video = query.data;
  const branding = video?.branding ?? null;
  const queryClient = useQueryClient();
  const [ended, setEnded] = useState(false);

  /**
   * May the person looking delete this?
   *
   * The share payload's `inLibrary` is not the answer: it is true for
   * everyone on the team, and the API only lets the owner and the team's
   * admins manage a recording. So the library row is asked for, which is the
   * one place that carries who owns it and which team it was made in. Only
   * fetched for people who have it in a library at all, so a visitor's page
   * makes no extra request.
   */
  const { org } = useOrg();
  const me = useQuery({ queryKey: ['me'], queryFn: fetchMe, enabled: Boolean(user), staleTime: 60_000 });
  const libraryId = video?.inLibrary ? video.videoId ?? null : null;
  const libraryRow = useQuery({
    queryKey: qk.video(libraryId ?? ''),
    queryFn: () => fetchVideo(libraryId as string),
    enabled: libraryId !== null,
    staleTime: 60_000,
  });
  const manageable =
    libraryRow.data !== undefined &&
    canManageVideo(libraryRow.data, { userId: me.data?.id ?? null, org });

  /**
   * A branded team's slates are played around the recording rather than
   * inside it: intro, then the recording, then outro, then the end card.
   *
   * The phase starts at 'intro' and falls through to 'main' the moment there
   * is no intro to play, so a team without slates takes exactly the path this
   * page always took.
   */
  const [phase, setPhase] = useState<'intro' | 'main' | 'outro'>('intro');
  const intro = branding?.intro ?? null;
  const outro = branding?.outro ?? null;
  // The recording is what plays unless there is an intro still to come. A
  // viewer who lands mid-page (a poll arriving late with branding on it) is
  // not thrown back to an intro they have gone past: only the initial phase
  // is decided here, and `ended` is what moves it on from there.
  const showIntro = phase === 'intro' && intro !== null;
  const showOutro = phase === 'outro' && outro !== null;
  // Theater: the player takes the whole width and the panel drops below,
  // like Loom and YouTube. Remembered per browser; it is a viewing habit.
  const [theater, setTheater] = useState(() => {
    try {
      return localStorage.getItem('slate.theater') === '1';
    } catch {
      return false;
    }
  });
  useEffect(() => {
    try {
      localStorage.setItem('slate.theater', theater ? '1' : '0');
    } catch {
      // Private mode. Fine.
    }
  }, [theater]);
  const [linkCopied, setLinkCopied] = useState(false);
  const copyLink = () => {
    // The team's own address when they have one, so the link a viewer copies
    // off this page reads the same as the one the owner sent.
    void navigator.clipboard.writeText(shareUrl(shareId, video?.branding?.domain)).then(() => {
      track('Share Link Copied', { video_id: shareId, surface: 'player' });
      setLinkCopied(true);
      window.setTimeout(() => setLinkCopied(false), 1800);
    });
  };
  // The Comment button and the C key both land in the composer, switching
  // the side panel to Activity if it is somewhere else.
  const [composerFocus, setComposerFocus] = useState(0);
  const [forcedTab, setForcedTab] = useState<{ key: string; at: number } | null>(null);
  const focusComposer = () => {
    setForcedTab({ key: 'comments', at: Date.now() });
    setComposerFocus((n) => n + 1);
  };
  // Reacting and commenting need a session. A visitor's first attempt opens
  // the prompt; what they were about to do runs once they are in.
  //
  // Except on a team's own domain, where there is no signing in: Firebase
  // trusts a fixed list of domains and a session is per-origin, so the prompt
  // there would fail for Google and mint a second session for anyone else.
  // The same recording on the app's own address is where that belongs, and
  // whoever is already signed in arrives there signed in.
  const [prompt, setPrompt] = useState<{ purpose: 'react' | 'comment'; then: () => void } | null>(null);
  const guarded = (purpose: 'react' | 'comment', then: () => void) => {
    if (user) then();
    else if (onCustomDomain()) window.location.assign(canonicalUrl(`/v/${shareId}`));
    else setPrompt({ purpose, then });
  };
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement | null;
      if (target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable)) return;
      if (event.key === 'c' && !event.metaKey && !event.ctrlKey && !event.altKey) {
        event.preventDefault();
        focusComposer();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);
  // Your own take is watchable the moment it lands, straight from the
  // original file; the processed version takes over when it exists.
  // Memoised on the URLs, not the object: the player reloads its source
  // whenever this changes, and a fresh object every render would restart
  // the video on each poll.
  // Every poll signs a fresh raw URL. Hold on to the first one so the
  // player is not handed a "new" source every five seconds; it is good for
  // fifteen minutes, and the processed version replaces it long before.
  const heldRawUrl = useRef<string | null>(null);
  if (video?.rawPlaybackUrl && !heldRawUrl.current) heldRawUrl.current = video.rawPlaybackUrl;
  if (video?.playback) heldRawUrl.current = null;
  const rawPlaybackUrl = video?.playback ? null : heldRawUrl.current;
  const rawPosterUrl = video?.posterUrl ?? null;
  const playable: Playback | null = useMemo(
    () =>
      video?.playback ??
      (rawPlaybackUrl
        ? {
            hlsUrl: null,
            mp4Url: rawPlaybackUrl,
            posterUrl: rawPosterUrl,
            captionsUrl: null,
          }
        : null),
    [video?.playback, rawPlaybackUrl, rawPosterUrl],
  );
  // Older rows predate `kind`; treat anything unexpected as a screen recording,
  // which is what the player has always assumed.
  const isAudio = video?.kind === 'audio';
  const mediaRef: RefObject<HTMLMediaElement | null> = isAudio ? audioRef : videoRef;
  const beacon = useViewBeacon(shareId, Boolean(video));
  // Same access rule as comments, and the same for both audiences: an
  // anonymous viewer on the public link and the signed-in owner are looking at
  // this exact page.
  const reactions = useReactions(shareId, Boolean(video));

  // The date it was made is the title until the transcript names it. While
  // the transcript is on its way, the page says so rather than presenting
  // "Recording Sep 17, 3:04 PM" as the last word.
  const improvingTitle = video?.titleSource === 'default' && video.transcript?.status === 'pending';

  // The page, once per recording. `gated` is whether an email stood in front
  // of it rather than whether one was given.
  const reported = useRef<string | null>(null);
  useEffect(() => {
    if (!video || reported.current === shareId) return;
    reported.current = shareId;
    track('Viewed Share Page', { video_id: shareId, gated: video.gate === 'require' });
  }, [shareId, video]);

  // The first play, whichever player is mounted: a video element and an audio
  // element both fire it.
  const played = useRef<string | null>(null);
  useEffect(() => {
    const media = mediaRef.current;
    if (!media) return;
    const onPlay = () => {
      if (played.current === shareId) return;
      played.current = shareId;
      track('Video Played', { video_id: shareId });
    };
    media.addEventListener('play', onPlay);
    return () => media.removeEventListener('play', onPlay);
  }, [mediaRef, playable, shareId]);

  useEffect(() => {
    if (video?.title) document.title = `${video.title} · ${branding?.name ?? 'Slate'}`;
    return () => {
      document.title = 'Slate';
    };
  }, [video?.title, branding?.name]);

  /**
   * A light page, when the team asked for one. Set on the document rather
   * than this component's root so the body behind it is lit the same way;
   * a dark strip under a white page on overscroll is exactly the kind of
   * detail that makes branding look bolted on. Removed on the way out,
   * because the next page in this single-page app is not theirs.
   */
  useEffect(() => {
    if (branding?.theme !== 'light') return;
    const root = document.documentElement;
    const previous = root.dataset.theme;
    root.dataset.theme = 'light';
    return () => {
      if (previous) root.dataset.theme = previous;
      else delete root.dataset.theme;
    };
  }, [branding?.theme]);

  // The tab's icon follows the team's square logo. Restored on the way out,
  // because this is a single-page app and the next page is not theirs.
  useEffect(() => {
    const square = branding?.squareLogoUrl;
    if (!square) return;
    const link = document.querySelector<HTMLLinkElement>('link[rel~="icon"]');
    if (!link) return;
    const original = link.href;
    link.href = square;
    return () => {
      link.href = original;
    };
  }, [branding?.squareLogoUrl]);

  return (
    <div
      className="flex min-h-full flex-col bg-slate-950"
      // Everything under here that carries `brand-fill`, `brand-button` or
      // `brand-text` picks these up; with no branding they are unset and the
      // classes fall back to chalk. See styles.css.
      style={
        branding?.brandColor
          ? ({
              '--brand': branding.brandColor,
              '--brand-ink': branding.brandInk ?? '#0b0d10',
            } as CSSProperties)
          : undefined
      }
    >
      <header className="border-b border-hairline">
        <div className="mx-auto flex h-14 w-full max-w-5xl items-center justify-between gap-3 px-4 sm:px-6">
          {branding?.wideLogoUrl || branding?.squareLogoUrl ? (
            // A branded team's own mark, and it goes nowhere: the viewer is
            // a customer of theirs, not somebody to send to a Slate login.
            <img
              src={branding.wideLogoUrl ?? (branding.squareLogoUrl as string)}
              alt={branding.name}
              className="max-h-8 w-auto max-w-[200px] object-contain"
            />
          ) : (
            <AppLink to={user ? '/' : '/login'} aria-label="Slate">
              <Wordmark />
            </AppLink>
          )}
          {/* On a team's own domain this leaves for the app's own address,
              and lands on this same recording rather than a bare sign-in
              page: whoever needs an account here is usually its owner,
              after their own settings. A plain link rather than a route,
              because it is a different origin, not another page of this one. */}
          {onCustomDomain() ? (
            <a href={canonicalUrl(`/v/${shareId}`)}>
              <Button size="sm">Open in Slate</Button>
            </a>
          ) : user ? (
            <AppLink to="/">
              <Button size="sm">Your library</Button>
            </AppLink>
          ) : (
            <AppLink to="/login">
              <Button size="sm">Log in</Button>
            </AppLink>
          )}
        </div>
        <ClapperRule />
      </header>

      <main className="mx-auto w-full max-w-5xl flex-1 px-4 py-6 sm:px-6 sm:py-8 lg:max-w-[1440px]">
        {query.isLoading ? <Loading label="Loading this recording" /> : null}

        {query.isError ? (
          <div className="mx-auto max-w-md py-16 text-center">
            <h1 className="text-h3 text-chalk">
              {query.error instanceof ApiError && query.error.isNotFound
                ? 'This link does not go anywhere'
                : 'That did not load'}
            </h1>
            <p className="mt-2 text-body text-chalk-dim">
              {query.error instanceof ApiError && query.error.isNotFound
                ? 'The recording may have been deleted, or the link may be private and meant for somebody signed in.'
                : errorMessage(query.error)}
            </p>
            {!user && query.error instanceof ApiError && query.error.isNotFound ? (
              <AppLink to="/login" className="mt-5 inline-block">
                <Button>Sign in and try again</Button>
              </AppLink>
            ) : null}
          </div>
        ) : null}

        {video ? (
          <div
            className={cx(
              'lg:grid lg:items-start lg:gap-8',
              theater ? 'lg:grid-cols-1' : 'lg:grid-cols-[minmax(0,1fr)_400px]',
            )}
          >
            <div className="min-w-0 space-y-5">
              {video.status === 'failed' ? (
                <Banner tone="danger" title="This recording failed to process">
                  Something went wrong converting it. If it is yours, the original upload is still
                  safe. Get in touch and it can be reprocessed.
                </Banner>
              ) : null}

              <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                <div className="min-w-0">
                  <h1
                    className={cx('text-h3 text-chalk', improvingTitle && 'shimmer-text')}
                    title={improvingTitle ? 'A title is being written from the transcript' : undefined}
                  >
                    {video.title}
                  </h1>
                  <p className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-caption text-chalk-faint">
                    {improvingTitle ? <span role="status">Naming this from the transcript</span> : null}
                    {isAudio ? (
                      <span className="flex items-center gap-1.5">
                        <WaveformIcon size={13} />
                        Audio note
                      </span>
                    ) : null}
                    {video.author?.name ? <span className="text-chalk-dim">{video.author.name}</span> : null}
                    <span title={dateTime(video.createdAt)}>{timeAgo(video.createdAt)}</span>
                    {video.durationSec ? (
                      <span data-timecode className="font-mono">
                        {timecode(video.durationSec)}
                      </span>
                    ) : null}
                    {video.status !== 'ready' ? (
                      <Badge tone="caution" pulse>
                        Processing
                      </Badge>
                    ) : video.rendering ? (
                      <Badge tone="caution" pulse>
                        Rendering your edit
                      </Badge>
                    ) : null}
                  </p>
                </div>
                <div className="flex shrink-0 items-center gap-2">
                  {video.inLibrary && video.videoId && video.status === 'ready' && !video.locked && video.editingIncluded ? (
                    video.rendering ? (
                      <Button size="sm" disabled title="The last edit is still rendering">
                        Edit
                      </Button>
                    ) : (
                      <AppLink to={`/edit/${video.videoId}`}>
                        <Button size="sm">Edit</Button>
                      </AppLink>
                    )
                  ) : null}
                  {video.viewer?.viewCount !== null && video.viewer?.viewCount !== undefined ? (
                    <span className="mr-1 text-caption text-chalk-dim">
                      {video.viewer.viewCount === 1 ? '1 view' : `${video.viewer.viewCount} views`}
                    </span>
                  ) : null}
                  <IconAction label={linkCopied ? 'Copied' : 'Copy link'} onClick={copyLink}>
                    <LinkIcon size={16} />
                  </IconAction>
                  {video.viewer?.downloadUrl ? (
                    <a
                      href={video.viewer.downloadUrl}
                      download
                      onClick={() => track('Video Downloaded', { video_id: shareId })}
                      aria-label="Download"
                      title="Download"
                      className="flex h-8 w-8 items-center justify-center rounded text-chalk-dim hover:bg-slate-800 hover:text-chalk"
                    >
                      <DownloadIcon size={16} />
                    </a>
                  ) : null}
                </div>
              </div>

              {video.locked ? (
                <PlanWall
                  title={video.title}
                  posterUrl={video.posterUrl}
                  limit={video.locked.limit}
                  owner={video.locked.owner}
                  authorName={video.author?.name ?? null}
                />
              ) : video.gate === 'require' ? (
                <EmailGate
                  shareId={shareId}
                  title={video.title}
                  posterUrl={video.posterUrl}
                  required
                  onDone={() => void query.refetch()}
                />
              ) : playable ? (
                isAudio ? (
                  <AudioPlayer
                    playback={playable}
                    title={video.title}
                    audioRef={audioRef}
                    durationHint={video.durationSec}
                    onProgress={beacon.report}
                    onEnded={beacon.flush}
                    reactions={reactions.reactions}
                  />
                ) : showIntro ? (
                  // The team's intro, in the frame the recording is about to
                  // appear in. Pressing play here is the gesture that lets the
                  // recording start on its own when this finishes.
                  <div data-theme="dark">
                    <SlateClip
                      slate={intro}
                      role="intro"
                      title={video.title}
                      onDone={() => setPhase('main')}
                    />
                  </div>
                ) : showOutro ? (
                  <div data-theme="dark">
                    <SlateClip
                      slate={outro}
                      role="outro"
                      onDone={() => {
                        setPhase('main');
                        setEnded(true);
                      }}
                    />
                  </div>
                ) : (
                  // The player keeps its own dark surface on a light page:
                  // its controls sit on top of the footage, and a player is
                  // dark wherever it appears.
                  <div className="relative" data-theme="dark">
                    <Player
                      playback={playable}
                      title={video.title}
                      videoRef={videoRef}
                      // Straight on from the intro the viewer just watched.
                      autoPlay={intro !== null && phase === 'main' && !ended}
                      onProgress={(current, duration) => {
                        beacon.report(current, duration);
                        if (ended && current < duration - 0.5) setEnded(false);
                      }}
                      onEnded={() => {
                        beacon.flush();
                        // The outro comes between the recording and the end
                        // card; without one, the end card follows directly.
                        if (outro) setPhase('outro');
                        else setEnded(true);
                      }}
                      reactions={reactions.reactions}
                      captionsDefault={video.viewer?.captionsOn}
                      initialRate={video.viewer?.playbackRate}
                      theater={theater}
                      onTheaterToggle={() => {
                        track('Theater Mode Toggled', { video_id: shareId, on: !theater });
                        setTheater((current) => !current);
                      }}
                      onCaptionsToggle={(on) => track('Captions Toggled', { video_id: shareId, on })}
                      onSpeedChange={(speed) => track('Playback Speed Changed', { video_id: shareId, speed })}
                    />
                    {ended && video.viewer?.commentsEnabled !== false ? (
                      <EndCard
                        shareId={shareId}
                        authorName={video.author?.name ?? null}
                        reactions={reactions}
                        reactionsEnabled={video.viewer?.reactionsEnabled !== false}
                        mediaRef={mediaRef}
                        onWatchAgain={() => {
                          setEnded(false);
                          // The recording again, not the intro: somebody
                          // asking to watch it again means the thing they
                          // came for.
                          setPhase('main');
                          const media = mediaRef.current;
                          if (media) {
                            media.currentTime = 0;
                            void media.play().catch(() => {});
                          }
                        }}
                        onCommented={() => void queryClient.invalidateQueries({ queryKey: qk.comments(shareId) })}
                        guard={guarded}
                      />
                    ) : null}
                  </div>
                )
              ) : (
                <ProcessingPanel title={video.title} audio={isAudio} posterUrl={video.posterUrl} />
              )}

              {video.gate === 'ask' ? (
                <EmailGate
                  shareId={shareId}
                  title={video.title}
                  posterUrl={null}
                  onDone={() => void query.refetch()}
                />
              ) : null}

              {!video.locked && video.gate !== 'require' && video.viewer?.reactionsEnabled !== false ? (
                <ReactionPill
                  state={reactions}
                  mediaRef={mediaRef}
                  ready={Boolean(playable)}
                  guard={guarded}
                  onComment={video.viewer?.commentsEnabled !== false ? () => guarded('comment', focusComposer) : undefined}
                />
              ) : null}

              {!video.locked && video.gate !== 'require' ? (
                <div className="grid gap-5 md:grid-cols-[minmax(0,3fr)_minmax(0,2fr)]">
                  <SummaryBlock
                    shareId={shareId}
                    videoId={video.inLibrary ? video.videoId ?? null : null}
                    summary={video.summary}
                    pending={video.transcript?.status === 'pending'}
                  />
                  <LinksBlock
                    shareId={shareId}
                    videoId={video.inLibrary ? video.videoId ?? null : null}
                    links={video.links ?? []}
                    atEnd={Boolean(video.viewer?.linksAtEnd) && !video.inLibrary}
                    mediaRef={mediaRef}
                  />
                </div>
              ) : null}
            </div>

            {/* Wide: a side panel next to the video. Narrow: the same panel
                under it. Tabs, because a transcript and a comment thread
                are both long and nobody reads them at once. */}
            {video.gate === 'require' || video.locked ? null : (
              <aside
                className={cx(
                  'mt-8 border-t border-hairline pt-4',
                  theater
                    ? 'lg:mx-auto lg:w-full lg:max-w-3xl'
                    : 'lg:mt-0 lg:max-h-[calc(100vh-8rem)] lg:overflow-y-auto lg:rounded-md lg:border lg:border-hairline lg:bg-slate-900/40 lg:px-5 lg:pb-5 lg:pt-3 lg:sticky lg:top-6',
                )}
              >
                <SidePanel
                  tabs={[
                    ...(video.viewer?.commentsEnabled !== false || video.inLibrary
                      ? [{ key: 'comments', label: 'Activity' }]
                      : []),
                    ...(video.transcript || (!video.transcriptIncluded && video.inLibrary)
                      ? [{ key: 'transcript', label: 'Transcript' }]
                      : []),
                    ...(video.settings && video.videoId
                      ? [{ key: 'settings', label: 'Settings' }]
                      : []),
                  ]}
                  initial="comments"
                  forced={forcedTab}
                  onChange={(next, previous) => {
                    if (next === 'transcript') track('Transcript Toggled', { video_id: shareId, open: true });
                    else if (previous === 'transcript') track('Transcript Toggled', { video_id: shareId, open: false });
                  }}
                >
                  {(tab) =>
                    tab === 'settings' && video.settings && video.videoId ? (
                      <>
                        <ShareSettings
                          videoId={video.videoId}
                          shareId={shareId}
                          settings={video.settings}
                        />
                        {manageable ? (
                          <DeleteRecording videoId={video.videoId} shareId={shareId} />
                        ) : null}
                      </>
                    ) : tab === 'transcript' ? (
                      video.transcript ? (
                        <Transcript
                          status={video.transcript.status}
                          captionsUrl={video.transcript.captionsUrl}
                          mediaRef={mediaRef}
                          title={video.title}
                          shareId={shareId}
                          fill
                        />
                      ) : (
                        <TranscriptUpsell />
                      )
                    ) : (
                      <Comments
                        shareId={shareId}
                        mediaRef={mediaRef}
                        playerReady={Boolean(playable)}
                        authorName={video.author?.name}
                        focusToken={composerFocus}
                        guard={guarded}
                      />
                    )
                  }
                </SidePanel>
              </aside>
            )}
          </div>
        ) : null}
      </main>

      {prompt ? (
        <SignInPrompt
          purpose={prompt.purpose}
          authorName={video?.author?.name ?? null}
          onClose={() => setPrompt(null)}
          onSignedIn={() => {
            const then = prompt.then;
            setPrompt(null);
            then();
          }}
        />
      ) : null}

      {/* The credit line, unless the team has paid to have their pages be
          entirely theirs. */}
      {branding?.hideSlateBranding ? null : (
        <footer className="border-t border-hairline">
          <div className="mx-auto flex w-full max-w-5xl items-center justify-between px-4 py-5 text-caption text-chalk-faint sm:px-6">
            <span>Recorded with Slate</span>
            <AppLink to="/signup" className="hover:text-chalk-dim">
              Make your own →
            </AppLink>
          </div>
        </footer>
      )}
    </div>
  );
}
